
import React from 'react';
import { Check, Sparkles, Zap, Shield, Crown, ArrowRight, Globe, Star } from 'lucide-react';
import { AppLanguage } from '../types';

interface UpgradeViewProps {
  isPro: boolean;
  onUpgrade: () => void;
  language: AppLanguage;
}

const UpgradeView: React.FC<UpgradeViewProps> = ({ isPro, onUpgrade }) => {
  const plans = [
    {
      name: 'Standard',
      price: '$0',
      model: 'Gemini 3 Flash',
      description: 'Fast, efficient AI for everyday tasks.',
      features: [
        '10 daily requests',
        'Standard reasoning speed',
        'Basic web search',
        'Zomi/English dictionary',
        'Community support'
      ],
      cta: 'Current Plan',
      active: !isPro,
      highlight: false,
      disabled: true
    },
    {
      name: 'ZomiGenAI Pro',
      price: '$20',
      model: 'Gemini 3 Pro',
      description: 'Advanced reasoning and high-fidelity generation.',
      features: [
        'Unlimited AI messaging',
        'Complex reasoning (Thinking Mode)',
        '2K/4K Image generation',
        '1080p Cinematic video synthesis',
        'Deep web research tool',
        'Premium voice synthesis',
        'Early access to new models'
      ],
      cta: isPro ? 'Active Subscription' : 'Upgrade to Pro',
      active: isPro,
      highlight: true,
      disabled: isPro
    }
  ];

  return (
    <div className="w-full h-full bg-white dark:bg-[#050505] overflow-y-auto selection:bg-blue-500/30 transition-colors duration-300">
      <div className="max-w-6xl mx-auto px-6 py-16 md:py-24">
        <div className="text-center mb-16 space-y-4">
          <div className="inline-flex items-center gap-2 px-3 py-1 bg-blue-600/10 border border-blue-500/20 rounded-full">
            <Sparkles size={14} className="text-blue-600" />
            <span className="text-[10px] font-bold text-blue-600 dark:text-blue-500 uppercase tracking-widest">Pricing Plans</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-black text-slate-900 dark:text-white tracking-tighter uppercase">
            The power of <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-indigo-600">Pro Reasoning</span>
          </h1>
          <p className="text-slate-500 dark:text-gray-500 text-lg max-w-2xl mx-auto font-medium">
            Choose the intelligence level that fits your workflow. From fast answers to deep cultural research.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-8 max-w-5xl mx-auto">
          {plans.map((plan) => (
            <div 
              key={plan.name}
              className={`relative flex flex-col p-8 md:p-10 rounded-[2.5rem] border transition-all duration-700 ${
                plan.highlight 
                ? 'bg-slate-50 dark:bg-[#0a0a0a] border-blue-100 dark:border-white/10 shadow-[0_32px_64px_-12px_rgba(0,0,0,0.05)] dark:shadow-[0_32px_64px_-12px_rgba(0,0,0,0.8)]' 
                : 'bg-transparent border-slate-200 dark:border-white/5'
              } ${plan.active && plan.highlight ? 'ring-2 ring-blue-600' : ''}`}
            >
              <div className="flex justify-between items-start mb-8">
                <div>
                  <h3 className="text-2xl font-bold text-slate-900 dark:text-white mb-2">{plan.name}</h3>
                  <div className="flex items-baseline gap-1">
                    <span className="text-4xl font-black text-slate-900 dark:text-white">{plan.price}</span>
                    <span className="text-slate-500 dark:text-gray-500 text-sm font-medium">/month</span>
                  </div>
                </div>
                {plan.highlight && <Crown className="text-amber-500" size={24} />}
              </div>

              <div className="mb-8">
                <div className="flex items-center gap-2 text-[10px] font-black text-blue-600 dark:text-blue-500 uppercase tracking-widest mb-2">
                  <Zap size={12} fill="currentColor" />
                  Core Engine: {plan.model}
                </div>
                <p className="text-sm text-slate-600 dark:text-gray-400 leading-relaxed font-medium">{plan.description}</p>
              </div>

              <button
                onClick={() => !plan.disabled && onUpgrade()}
                disabled={plan.disabled}
                className={`w-full py-4 rounded-2xl font-black text-xs uppercase tracking-widest transition-all mb-10 active:scale-[0.98] ${
                  plan.highlight 
                    ? 'bg-blue-600 hover:bg-blue-500 text-white shadow-xl shadow-blue-900/20 disabled:bg-slate-200 dark:disabled:bg-white/5 disabled:text-slate-400 dark:disabled:text-white/20' 
                    : 'bg-slate-100 dark:bg-white/5 text-slate-400 dark:text-white/20 cursor-default'
                }`}
              >
                {plan.cta}
              </button>

              <div className="space-y-4 flex-1">
                {plan.features.map((feature, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <Check size={16} className={plan.highlight ? 'text-blue-600' : 'text-slate-300 dark:text-gray-600'} />
                    <span className={`text-sm font-semibold ${plan.active ? 'text-slate-900 dark:text-white/80' : 'text-slate-400 dark:text-gray-500'}`}>
                      {feature}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>

        <div className="mt-24 pt-12 border-t border-slate-200 dark:border-white/5 flex flex-col items-center">
          <div className="flex flex-wrap justify-center gap-8 mb-12 opacity-40 grayscale hover:grayscale-0 transition-all duration-500">
             <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-xs uppercase tracking-widest">
               <Shield size={16} /> Secure Payment
             </div>
             <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-xs uppercase tracking-widest">
               <Globe size={16} /> Global CDN
             </div>
             <div className="flex items-center gap-2 text-slate-900 dark:text-white font-bold text-xs uppercase tracking-widest">
               <Star size={16} /> cancel anytime
             </div>
          </div>
            <p className="text-[9px] text-slate-200 dark:text-gray-800 font-bold uppercase tracking-[0.5em]">
            Zomi Generative AI Neural Network Infrastructure 2025
          </p>
        </div>
      </div>
    </div>
  );
};

export default UpgradeView;
